import { ref, type Component, type Ref } from 'vue';

import {
  AssetContainerType,
  AssetVisitType,
  Nullable,
  type IAppointment,
  type ICustomField,
  type IWarehouse
} from '@nova/core';

import CheckinDetailsPage from '@/views/CheckinDetailsPage.vue';
import IdVerificationPage from '@/views/IdVerificationPage.vue';
import PurposeOfStopPage from '@/views/PurposeOfStopPage.vue';
import WelcomePage from '@/views/WelcomePage.vue';

export enum CheckinFlowStep {
  Welcome = 'Welcome',
  PurposeOfStop = 'PurposeOfStop',
  CheckinDetailsPage = 'CheckinDetailsPage',
  IdVerification = 'IdVerification',
  Summary = 'Summary'
}

export type MessagingType = 'SMS' | 'WhatsApp';

export interface AssetContainerData {
  containerCode: string;
  containerType: AssetContainerType;
}

export interface CustomFormDatum {
  formFieldId: string;
  triggerId: string;
  value: ICustomField['value'];
}

const step = ref<CheckinFlowStep>(CheckinFlowStep.Welcome);
const previousStep = ref<Nullable<CheckinFlowStep>>(null);
const isStepLoading = ref<boolean>(false);
const appointmentId = ref<Nullable<string>>(null);
const assetVisitId = ref<Nullable<string>>(null);
const assetVisitType = ref<Nullable<AssetVisitType>>(null);
const loadTypeId = ref<Nullable<string>>(null);
const identifier = ref<Nullable<string>>(null);
const latitude = ref<Nullable<number>>(null);
const longitude = ref<Nullable<number>>(null);
const inquiryId = ref<Nullable<string>>(null);
const companyId = ref<Nullable<string>>(null);
const companyName = ref<Nullable<string>>(null);
const phoneNumber = ref<Nullable<string>>(null);
const useWhatsApp = ref<boolean>(false);
const messagingType = ref<Nullable<MessagingType>>(null);
const notes = ref<string>('');
const licensePlate = ref<Nullable<string>>(null);
const dotNumber = ref<Nullable<string>>(null);
const assetContainerData = ref<AssetContainerData[]>([
  { containerCode: '', containerType: AssetContainerType.Trailer }
]);
const customFormsData = ref<CustomFormDatum[]>([]);
const warehouse = ref<Partial<IWarehouse>>({});
const appointment = ref<Nullable<IAppointment>>(null);

const stepValidation = ref<Record<CheckinFlowStep, boolean>>({
  [CheckinFlowStep.Welcome]: true,
  [CheckinFlowStep.PurposeOfStop]: false,
  [CheckinFlowStep.CheckinDetailsPage]: false,
  [CheckinFlowStep.IdVerification]: false,
  [CheckinFlowStep.Summary]: false
});

export function useCheckinFlow() {
  const getCurrentStepComponent = (currentStep: Ref<CheckinFlowStep>): Component | null => {
    switch (currentStep.value) {
      case CheckinFlowStep.Welcome:
        return WelcomePage;
      case CheckinFlowStep.PurposeOfStop:
        return PurposeOfStopPage;
      case CheckinFlowStep.CheckinDetailsPage:
        return CheckinDetailsPage;
      case CheckinFlowStep.IdVerification:
        return IdVerificationPage;
      case CheckinFlowStep.Summary:
      default:
        return null;
    }
  };

  const setStepValid = (stepName: CheckinFlowStep, isValid: boolean) => {
    stepValidation.value[stepName] = isValid;
  };

  const isStepValid = (stepName: CheckinFlowStep) => {
    return stepValidation.value[stepName] || false;
  };

  const setNextStep = (nextStep: CheckinFlowStep) => {
    previousStep.value = step.value;
    step.value = nextStep;
  };

  const setPreviousStep = (newStep: CheckinFlowStep) => {
    previousStep.value = step.value;
    step.value = newStep;
  };

  const setIsStepLoading = (loading: boolean) => {
    isStepLoading.value = loading;
  };

  const setAppointmentId = (id: Nullable<string>) => {
    appointmentId.value = id;
  };

  const setAssetVisitId = (id: Nullable<string>) => {
    assetVisitId.value = id;
  };

  const setAssetVisitType = (type: Nullable<AssetVisitType>) => {
    assetVisitType.value = type;
  };

  const setLoadTypeId = (id: Nullable<string>) => {
    loadTypeId.value = id;
  };

  const setIdentifier = (value: Nullable<string>) => {
    identifier.value = value;
  };

  const setLocation = (lat: Nullable<number>, lng: Nullable<number>) => {
    latitude.value = lat;
    longitude.value = lng;
  };

  const setInquiryId = (id: Nullable<string>) => {
    inquiryId.value = id;
  };

  const setCompany = (id: Nullable<string>, name: Nullable<string>) => {
    companyId.value = id;
    companyName.value = name;
  };

  const setPhoneNumber = (value: Nullable<string>) => {
    phoneNumber.value = value;
  };

  const setUseWhatsApp = (value: boolean) => {
    useWhatsApp.value = value;
  };

  const setMessagingType = (value: Nullable<MessagingType>) => {
    messagingType.value = value;
  };

  const setNotes = (value: string) => {
    notes.value = value;
  };

  const setLicensePlate = (value: Nullable<string>) => {
    licensePlate.value = value;
  };

  const setDotNumber = (value: Nullable<string>) => {
    dotNumber.value = value;
  };

  const setAssetContainerData = (value: AssetContainerData[]) => {
    assetContainerData.value = value;
  };

  const setCustomFormsData = (value: CustomFormDatum[]) => {
    customFormsData.value = value;
  };

  const setWarehouse = (value: Partial<IWarehouse>) => {
    warehouse.value = value;
  };

  const setAppointment = (value: Nullable<IAppointment>) => {
    appointment.value = value;
  };

  const resetCheckinFlow = () => {
    step.value = CheckinFlowStep.Welcome;
    previousStep.value = null;
    isStepLoading.value = false;
    appointmentId.value = null;
    assetVisitId.value = null;
    assetVisitType.value = null;
    loadTypeId.value = null;
    identifier.value = null;
    latitude.value = null;
    longitude.value = null;
    inquiryId.value = null;
    companyId.value = null;
    companyName.value = null;
    phoneNumber.value = null;
    useWhatsApp.value = false;
    messagingType.value = null;
    notes.value = '';
    licensePlate.value = null;
    dotNumber.value = null;
    assetContainerData.value = [{ containerCode: '', containerType: AssetContainerType.Trailer }];
    customFormsData.value = [];
    warehouse.value = {};
    appointment.value = null;
    stepValidation.value = {
      [CheckinFlowStep.Welcome]: true,
      [CheckinFlowStep.PurposeOfStop]: false,
      [CheckinFlowStep.CheckinDetailsPage]: false,
      [CheckinFlowStep.IdVerification]: false,
      [CheckinFlowStep.Summary]: false
    };
  };

  return {
    step,
    previousStep,
    isStepLoading,
    appointmentId,
    assetVisitId,
    assetVisitType,
    loadTypeId,
    identifier,
    latitude,
    longitude,
    inquiryId,
    companyId,
    companyName,
    phoneNumber,
    useWhatsApp,
    messagingType,
    notes,
    licensePlate,
    dotNumber,
    assetContainerData,
    customFormsData,
    warehouse,
    appointment,
    getCurrentStepComponent,
    setStepValid,
    isStepValid,
    setNextStep,
    setPreviousStep,
    setIsStepLoading,
    setAppointmentId,
    setAssetVisitId,
    setAssetVisitType,
    setLoadTypeId,
    setIdentifier,
    setLocation,
    setInquiryId,
    setCompany,
    setPhoneNumber,
    setUseWhatsApp,
    setMessagingType,
    setNotes,
    setLicensePlate,
    setDotNumber,
    setAssetContainerData,
    setCustomFormsData,
    setWarehouse,
    setAppointment,
    resetCheckinFlow
  };
}
